import type { Metadata } from "next";
import { DEFAULT_SHIPPING_SETTINGS } from "@/features/shipping/shared/defaults";
import {
  buildSiteMetaShippingSnippet,
  getMarketTransitWindow,
} from "@/features/shipping/shared/messaging";
import { getEnabledShippingMethods } from "@/features/shipping/shared/shippingFee";
import type { ShippingSettings } from "@/features/shipping/shared/types";
import { indexableLocales } from "@/i18n/staticGeneration";
import { BaseUrl, InfoEmail, locales } from "@/lib/shared/constants";

const siteName = "TUTULY";
const maxPageTitleLength = 60 - ` | ${siteName}`.length;
const defaultLocale = "en";
const structuredDataCurrency = "USD";
const socialProfileUrls = [
  "https://www.instagram.com/tutulyjewellery",
  "https://www.tiktok.com/@tutulyjewellery",
] as const;

/** Market-specific metadata overrides for detection-based targeting. */
export type MarketCode = "US" | "UK" | null;

export function getMarketMetadata(
  market: MarketCode,
  shippingSettings: ShippingSettings = DEFAULT_SHIPPING_SETTINGS,
): {
  title: string;
  description: string;
  keywords: string[];
} {
  const supportWindowDays = shippingSettings.returnWindowDays;
  const shippingSnippet = buildSiteMetaShippingSnippet(shippingSettings);

  if (market === "US") {
    return {
      title: "Modern Pearl & Sterling Silver Jewelry",
      description: `Shop TUTULY for modern pearl jewelry, sterling silver jewelry, and vintage-inspired designs. ${shippingSnippet}, complimentary gift packaging, and support for damaged or defective items reported within ${supportWindowDays} days of delivery.`,
      keywords: [
        "pearl jewelry",
        "sterling silver jewelry",
        "modern pearl earrings",
        "sterling silver gifts",
        "jewelry under $100",
        "pearl necklace sterling silver",
        "vintage inspired jewelry",
        "gifts for her",
        "wedding guest jewelry",
      ],
    };
  }

  // UK default when market is "UK" (also works for null/unknown)
  return {
    title: "Modern Pearl & Sterling Silver Jewellery",
    description: `Shop TUTULY for elegant pearl jewellery, sterling silver jewellery, and vintage-inspired designs. ${shippingSnippet}, complimentary gift packaging with every order, and support for damaged or defective items reported within ${supportWindowDays} days of delivery.`,
    keywords: [
      "pearl jewellery",
      "sterling silver jewellery",
      "modern pearl earrings",
      "sterling silver gifts",
      "jewellery under £80",
      "pearl necklace sterling silver",
      "vintage inspired jewellery",
      "gifts for her",
      "wedding guest jewellery",
    ],
  };
}

/** Public path to the brand logo (served from `public/brand/`). */
export const brandLogoPathname = "/brand/tutuly-jewellery-logo.png";
const rootDisallowPaths = ["/admin"] as const;
const noIndexRoutePaths = [
  "/checkout",
  "/orders/success",
  "/products/compare",
  "/user/profile",
  "/user/orders",
  "/user/wishlist",
  "/user/reset-password",
  "/affiliate/",
] as const;

export const noIndexRobots: NonNullable<Metadata["robots"]> = {
  index: false,
  follow: false,
  nocache: true,
  googleBot: {
    index: false,
    follow: false,
    noimageindex: true,
  },
};

/** Keeps a page out of the index while still letting crawlers follow its links. */
export const noIndexFollowRobots: NonNullable<Metadata["robots"]> = {
  index: false,
  follow: true,
  googleBot: {
    index: false,
    follow: true,
  },
};

export function normalizePathname(pathname: string): string {
  const trimmed = pathname.trim();
  if (!trimmed || trimmed === "/") return "/";

  const normalized = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  return normalized.length > 1 && normalized.endsWith("/") ? normalized.slice(0, -1) : normalized;
}

export function getMetadataBase(baseUrl = BaseUrl): URL | undefined {
  if (!baseUrl) return undefined;

  try {
    return new URL(baseUrl);
  } catch {
    return undefined;
  }
}

export function getBrandLogoAbsoluteUrl(baseUrl = BaseUrl): string {
  const metadataBase = getMetadataBase(baseUrl) ?? new URL("http://localhost:3000");
  return new URL(brandLogoPathname, metadataBase).toString();
}

type SiteMetadataInput = {
  title: string;
  description: string;
  baseUrl?: string;
};

const maxMetaDescriptionLength = 155;

export function truncatePageTitle(value: string, maxLength = maxPageTitleLength): string {
  const normalized = value.replace(/\s+/g, " ").trim();
  if (normalized.length <= maxLength) return normalized;

  const content = normalized.slice(0, maxLength);
  const wordEnd = content.lastIndexOf(" ");
  return content
    .slice(0, wordEnd > 0 ? wordEnd : content.length)
    .replace(/\s+(?:a|an|and|for|of|the|to|with)$/i, "")
    .trim();
}

export function truncateMetaDescription(
  value: string | undefined,
  maxLength = maxMetaDescriptionLength,
): string | undefined {
  if (!value) return value;

  const normalized = value.replace(/\s+/g, " ").trim();
  if (normalized.length <= maxLength) return normalized;

  const slice = normalized.slice(0, maxLength);
  const sentenceEnd = Math.max(
    slice.lastIndexOf("."),
    slice.lastIndexOf("!"),
    slice.lastIndexOf("?"),
  );
  if (sentenceEnd >= 80) return slice.slice(0, sentenceEnd + 1).trim();

  const content = normalized.slice(0, maxLength - 3);
  const wordEnd = content.lastIndexOf(" ");
  return `${content.slice(0, wordEnd > 80 ? wordEnd : content.length).trim()}...`;
}

export function buildSiteMetadata({ title, description, baseUrl }: SiteMetadataInput): Metadata {
  const defaultSocialImage = getBrandLogoAbsoluteUrl(baseUrl);
  const metaDescription = truncateMetaDescription(description);
  return {
    metadataBase: getMetadataBase(baseUrl),
    manifest: "/manifest.json",
    title: {
      default: title,
      template: `%s | ${siteName}`,
    },
    description: metaDescription,
    openGraph: {
      title,
      description: metaDescription,
      type: "website",
      siteName,
      images: [defaultSocialImage],
    },
    twitter: {
      card: "summary_large_image",
      title,
      description: metaDescription,
      images: [defaultSocialImage],
    },
  };
}

export function buildLocalePathname(locale: string, pathname: string): string {
  const normalizedPath = normalizePathname(pathname);
  return normalizedPath === "/" ? `/${locale}` : `/${locale}${normalizedPath}`;
}

export function buildAlternateLinks(
  locale: string,
  pathname: string,
  supportedLocales: readonly string[] = indexableLocales,
): NonNullable<Metadata["alternates"]> {
  const languages = Object.fromEntries(
    supportedLocales.map((supportedLocale) => [
      supportedLocale,
      buildLocalePathname(supportedLocale, pathname),
    ]),
  );

  // Add region-specific hreflang for en-US and en-GB pointing to the same /en/ page
  return {
    canonical: buildLocalePathname(locale, pathname),
    languages: {
      ...languages,
      "en-US": buildLocalePathname(defaultLocale, pathname),
      "en-GB": buildLocalePathname(defaultLocale, pathname),
      "x-default": buildLocalePathname(defaultLocale, pathname),
    },
  };
}

type PageMetadataInput = {
  locale: string;
  pathname: string;
  title: string;
  description?: string;
  keywords?: string[] | string;
  robots?: Metadata["robots"];
  /** Locales listed in hreflang; defaults to every indexable locale. */
  alternateLocales?: readonly string[];
  openGraphType?: "website" | "article";
  images?: string[];
  /** ISO 8601; use for `article` Open Graph type. */
  openGraphPublishedTime?: string;
  /** ISO 8601; use for `article` Open Graph type. */
  openGraphModifiedTime?: string;
  baseUrl?: string;
};

export function buildPageMetadata({
  locale,
  pathname,
  title,
  description,
  keywords,
  robots,
  alternateLocales,
  openGraphType = "website",
  images,
  openGraphPublishedTime,
  openGraphModifiedTime,
  baseUrl = BaseUrl,
}: PageMetadataInput): Metadata {
  const pageUrl = getLocaleUrl(locale, pathname, baseUrl);
  const pageTitle = truncatePageTitle(title);
  const metaDescription = truncateMetaDescription(description);
  const openGraph: Metadata["openGraph"] = {
    url: pageUrl,
    title: pageTitle,
    description: metaDescription,
    type: openGraphType,
    locale,
    siteName,
    images,
    ...(openGraphPublishedTime ? { publishedTime: openGraphPublishedTime } : {}),
    ...(openGraphModifiedTime ? { modifiedTime: openGraphModifiedTime } : {}),
  };

  return {
    title: pageTitle,
    description: metaDescription,
    keywords,
    alternates: buildAlternateLinks(locale, pathname, alternateLocales),
    robots,
    openGraph,
    twitter: {
      card: "summary_large_image",
      title: pageTitle,
      description: metaDescription,
      images,
    },
  };
}

function stripHtmlTags(value: string): string {
  return value
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+([,.;!?])/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
}

type FAQJsonLdItem = {
  question: string;
  answer: string;
};

type JsonLdBaseInput = {
  locale: string;
  baseUrl?: string;
};

type BreadcrumbItem = {
  name: string;
  pathname: string;
};

type ItemListJsonLdItem = {
  name: string;
  pathname: string;
  image?: string;
  description?: string;
};

type ProductJsonLdInput = JsonLdBaseInput & {
  pathname: string;
  description?: string;
  market?: MarketCode;
  /** Currency and rate used by the rendered storefront market. Prices are stored in CNY. */
  currencyCode?: string;
  priceExchangeRate?: number;
  shippingSettings?: ShippingSettings;
  product: {
    name: string;
    sku: string;
    images?: Array<{ src: string; alt?: string | null }>;
    variants: Array<{ price: number; onSalePrice?: number | null; stock?: number | null }>;
    reviews?: Array<{
      review?:
        | {
            rating: number;
            author?: string;
            content?: string;
            createdAt?: string;
          }
        | string
        | null;
    }> | null;
  };
};

type ProductReviewSchemaInput = {
  review?:
    | {
        rating: number;
        author?: string;
        content?: string;
        createdAt?: string;
      }
    | string
    | null;
};

type ProductReviewDetails = Exclude<ProductReviewSchemaInput["review"], string | null | undefined>;

type BlogPostingJsonLdInput = JsonLdBaseInput & {
  pathname: string;
  description?: string;
  blog: {
    title: string;
    createdAt: string;
    updatedAt: string;
    headerImage?: { src: string; alt?: string | null };
  };
};

function toAbsoluteUrl(pathname: string, baseUrl = BaseUrl): string {
  const metadataBase = getMetadataBase(baseUrl) ?? new URL("http://localhost:3000");
  const normalizedPath = normalizePathname(pathname);
  return new URL(normalizedPath, metadataBase).toString();
}

function getLocaleUrl(locale: string, pathname: string, baseUrl = BaseUrl): string {
  return toAbsoluteUrl(buildLocalePathname(locale, pathname), baseUrl);
}

function isProductReviewDetails(
  review: ProductReviewSchemaInput["review"],
): review is ProductReviewDetails {
  return typeof review === "object" && review !== null && "rating" in review;
}

export function buildFaqJsonLd(locale: string, items: FAQJsonLdItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    inLanguage: locale,
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: stripHtmlTags(item.answer),
      },
    })),
  };
}

export function buildOrganizationJsonLd({
  locale,
  description,
  baseUrl,
}: JsonLdBaseInput & { description: string }) {
  const logoUrl = getBrandLogoAbsoluteUrl(baseUrl);
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: siteName,
    url: toAbsoluteUrl("/", baseUrl),
    logo: {
      "@type": "ImageObject",
      url: logoUrl,
    },
    sameAs: [...socialProfileUrls],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer support",
      email: InfoEmail,
      availableLanguage: [...indexableLocales],
    },
    description,
    inLanguage: locale,
  };
}

export function buildWebSiteJsonLd({ locale, baseUrl }: JsonLdBaseInput) {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: siteName,
    url: getLocaleUrl(locale, "/", baseUrl),
    inLanguage: locale,
  };
}

type WebPageJsonLdInput = JsonLdBaseInput & {
  pathname: string;
  title: string;
  description?: string;
};

export function buildWebPageJsonLd({
  locale,
  pathname,
  title,
  description,
  baseUrl,
}: WebPageJsonLdInput) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: title,
    ...(description && { description }),
    url: getLocaleUrl(locale, pathname, baseUrl),
    inLanguage: locale,
  };
}

export function buildCollectionPageJsonLd({
  locale,
  pathname,
  title,
  description,
  baseUrl,
}: WebPageJsonLdInput) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: title,
    ...(description && { description }),
    url: getLocaleUrl(locale, pathname, baseUrl),
    inLanguage: locale,
  };
}

export function buildItemListJsonLd(
  locale: string,
  items: ItemListJsonLdItem[],
  baseUrl = BaseUrl,
) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      url: getLocaleUrl(locale, item.pathname, baseUrl),
      item: {
        "@type": "Thing",
        name: item.name,
        url: getLocaleUrl(locale, item.pathname, baseUrl),
        ...(item.image ? { image: item.image } : {}),
        ...(item.description ? { description: item.description } : {}),
      },
    })),
  };
}

export function buildBreadcrumbJsonLd(locale: string, items: BreadcrumbItem[], baseUrl = BaseUrl) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: getLocaleUrl(locale, item.pathname, baseUrl),
    })),
  };
}

export function buildProductJsonLd({
  locale,
  pathname,
  description,
  product,
  market,
  currencyCode: inputCurrencyCode,
  priceExchangeRate = 1,
  baseUrl,
  shippingSettings = DEFAULT_SHIPPING_SETTINGS,
}: ProductJsonLdInput) {
  // Match the initial PDP render, which selects the first variant before any user interaction.
  const primaryVariant = product.variants[0];
  const primaryOffer = primaryVariant
    ? {
        price: primaryVariant.onSalePrice ?? primaryVariant.price,
        availability:
          typeof primaryVariant.stock === "number" && primaryVariant.stock <= 0
            ? "https://schema.org/OutOfStock"
            : "https://schema.org/InStock",
      }
    : undefined;

  const ratings = (product.reviews ?? [])
    .map((item) =>
      typeof item.review === "object" && item.review ? item.review.rating : undefined,
    )
    .filter((rating): rating is number => typeof rating === "number");
  const ratingValue = ratings.length
    ? Number((ratings.reduce((total, rating) => total + rating, 0) / ratings.length).toFixed(1))
    : undefined;

  const eligibleCountry = market === "US" ? "US" : market === "UK" ? "GB" : undefined;
  const priceCurrency = inputCurrencyCode ?? (market === "UK" ? "GBP" : structuredDataCurrency);
  const fractionDigits = priceCurrency === "JPY" ? 0 : 2;
  const convertPrice = (value: number) =>
    Number((value * priceExchangeRate).toFixed(fractionDigits));
  const countryCodeForTransit = market === "UK" ? "UK" : market === "US" ? "US" : undefined;
  const transit = getMarketTransitWindow(shippingSettings, countryCodeForTransit);
  const standardMethod =
    getEnabledShippingMethods(shippingSettings).find((method) => method.methodId === "standard") ??
    shippingSettings.shippingMethods[0];
  const expressMethod = getEnabledShippingMethods(shippingSettings).find(
    (method) => method.methodId === "express",
  );

  const buildOfferShippingDetail = (fee: number, min: number, max: number) => ({
    "@type": "OfferShippingDetails",
    shippingRate: {
      "@type": "MonetaryAmount",
      value: convertPrice(fee),
      currency: priceCurrency,
    },
    shippingDestination: {
      "@type": "DefinedRegion",
      addressCountry: "Worldwide",
    },
    deliveryTime: {
      "@type": "ShippingDeliveryTime",
      transitTime: {
        "@type": "QuantitativeValue",
        minValue: min,
        maxValue: max,
        unitCode: "DAY",
      },
    },
  });

  const shippingDetails =
    expressMethod?.enabled && expressMethod
      ? [
          buildOfferShippingDetail(
            standardMethod?.fee ?? 0,
            standardMethod?.transitDaysMin ?? transit.min,
            standardMethod?.transitDaysMax ?? transit.max,
          ),
          buildOfferShippingDetail(
            expressMethod.fee,
            expressMethod.transitDaysMin,
            expressMethod.transitDaysMax,
          ),
        ]
      : buildOfferShippingDetail(
          standardMethod?.fee ?? 0,
          standardMethod?.transitDaysMin ?? transit.min,
          standardMethod?.transitDaysMax ?? transit.max,
        );

  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description,
    sku: product.sku,
    image: product.images?.map((image) => image.src),
    url: getLocaleUrl(locale, pathname, baseUrl),
    brand: {
      "@type": "Brand",
      name: siteName,
    },
    offers: primaryOffer
      ? {
          "@type": "Offer",
          price: convertPrice(primaryOffer.price),
          // Storefront catalog prices are authored in CNY before market conversion.
          priceCurrency,
          availability: primaryOffer.availability,
          url: getLocaleUrl(locale, pathname, baseUrl),
          ...(eligibleCountry && {
            eligibleRegion: {
              "@type": "Country",
              name: eligibleCountry,
            },
          }),
          shippingDetails,
          hasMerchantReturnPolicy: {
            "@type": "MerchantReturnPolicy",
            applicableCountry: "Worldwide",
            returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
            merchantReturnDays: shippingSettings.returnWindowDays,
            returnMethod: "https://schema.org/ReturnByMail",
          },
        }
      : undefined,
    aggregateRating:
      ratingValue != null
        ? {
            "@type": "AggregateRating",
            ratingValue,
            reviewCount: ratings.length,
          }
        : undefined,
    review: (product.reviews ?? [])
      .map((item) => item.review)
      .filter(isProductReviewDetails)
      .map((review) => ({
        "@type": "Review",
        author: {
          "@type": "Person",
          name: review.author ?? "Anonymous",
        },
        reviewBody: review.content,
        reviewRating: {
          "@type": "Rating",
          ratingValue: review.rating,
        },
        datePublished: review.createdAt,
      })),
  };
}

export function buildBlogPostingJsonLd({
  locale,
  pathname,
  description,
  blog,
  baseUrl,
}: BlogPostingJsonLdInput) {
  const publisherLogoUrl = getBrandLogoAbsoluteUrl(baseUrl);
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: blog.title,
    description,
    image: blog.headerImage?.src ? [blog.headerImage.src] : undefined,
    datePublished: blog.createdAt,
    dateModified: blog.updatedAt,
    mainEntityOfPage: getLocaleUrl(locale, pathname, baseUrl),
    author: {
      "@type": "Organization",
      name: siteName,
    },
    publisher: {
      "@type": "Organization",
      name: siteName,
      logo: {
        "@type": "ImageObject",
        url: publisherLogoUrl,
      },
    },
    inLanguage: locale,
  };
}

/** Disallow entire locale trees that are not indexed (e.g. `/es/`, `/fr/`). */
export function buildNonIndexableLocaleDisallowPaths(
  allLocales: readonly string[] = locales,
  indexedLocales: readonly string[] = indexableLocales,
): string[] {
  const indexed = new Set(indexedLocales);
  return allLocales.filter((locale) => !indexed.has(locale)).map((locale) => `/${locale}/`);
}

export function buildRobotsDisallowPaths(
  supportedLocales: readonly string[] = indexableLocales,
): string[] {
  const localizedDisallowPaths = supportedLocales.flatMap((locale) =>
    noIndexRoutePaths.map((pathname) => {
      if (pathname.endsWith("/")) {
        return `${buildLocalePathname(locale, pathname.slice(0, -1))}/`;
      }

      return buildLocalePathname(locale, pathname);
    }),
  );

  return [
    ...rootDisallowPaths,
    ...buildNonIndexableLocaleDisallowPaths(),
    ...localizedDisallowPaths,
  ];
}

export function buildStaticSitemapPaths(
  supportedLocales: readonly string[] = indexableLocales,
  publicPaths: string[],
): string[] {
  return publicPaths.flatMap((pathname) =>
    supportedLocales.map((locale) => buildLocalePathname(locale, pathname)),
  );
}
